import React, {Fragment, useCallback, useEffect, useRef, useState} from 'react';
import {
    Alert,
    Button,
    Card,
    CardContent,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    LinearProgress,
    Toolbar,
    Typography
} from '@mui/material';
import Config from '../../config';
import axios from 'axios';
import EnhancedTable from '../../components/table';
import {Field as FieldBase, Form as FormBase} from 'react-final-form';
import DownloadButton from '../../components/download_button';
import TextField from '../../components/text_field';
import {LabelledSwitch as SwitchField} from '../../components/switch';
import AutoComplete from '../../components/suggest_field';
import {ReturnCodes, ReturnReasonOptions} from '../documents/return_details';
import {ChangeReasonOptions} from '../documents/ack_details';
import Helpers, {clearDefaultSettingsCache} from '../../helpers';
import {reportError} from '../../utils/report_error';
import {FORM_ERROR} from 'final-form';
import {loadProfiles} from '../files/import_export_profiles';
import ContentLoader from '../../components/content_loader';
import EventModal from '../messages/event_modal';
import EventTable from '../../components/event_table';
// Switch styling moved to inline sx prop
import {FormEnhancedSelect} from "../catalogmanager/containers/settings/containers/auto_approve";
import {listingStatusOptions} from "../../utils/utils";
import {useHistory} from "react-router";
import Auth from '../../auth';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (established pattern, see
// reports.tsx/scorecards.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;

const identity = (v: any) => (v);

// `hidden` describes the property's partner-table column, not its input (see
// setting_modifier). It is a real HTML attribute, so spreading it onto the
// Field would hide the setting itself rather than its column.
const toFieldProps = ({hidden, ...rest}: any) => rest;

// Backend caps each value on the global config bag at this length.
export const GLOBAL_CONFIG_MAX_LENGTH = 2000;

export const OVERSIZED_SETTING_ERROR = `The attribute list is too long to save (limit ${GLOBAL_CONFIG_MAX_LENGTH} characters). Remove some attributes and try again.`;

const GENERIC_SAVE_ERROR = 'Unexpected error occurred.';

// OData sometimes nests the message as {value: '...'}; a non-string would break
// React's child reconciliation when rendered into the error Alert.
const asMessage = (value: any): string | undefined => {
    if(typeof value === 'string' && value.trim()){
        return value;
    }
    if(value && typeof value.value === 'string' && value.value.trim()){
        return value.value;
    }
    return undefined;
};

// The endpoint's error body is not pinned down, so try each shape this codebase
// has seen before falling back to the transport error.
export const extractSaveError = (error: any): string => {
    const data = error && error.response ? error.response.data : undefined;
    if(data){
        const odataMessage = asMessage(data.error && data.error.message);
        if(odataMessage){
            return odataMessage;
        }
        const topLevelMessage = asMessage(data.Message);
        if(topLevelMessage){
            return topLevelMessage;
        }
        // getApiErrors substitutes the generic message rather than returning
        // empty, so drop that filler and keep looking.
        const apiErrors = new Helpers().getApiErrors(data)
            .filter((r: string) => r && r !== GENERIC_SAVE_ERROR);
        if(apiErrors.length > 0){
            return apiErrors.join('\n');
        }
    }
    return asMessage(error && error.message) || GENERIC_SAVE_ERROR;
};

const checkboxTemplate = (value: any) => <Checkbox checked={value === 'true' || value === true}/>;

const validateNumber = (v: any) => {
    if(v && (isNaN(v) || isNaN(parseFloat(v)))){
        return `Must be a number.`;
    }
    return undefined;
}

type DefaultPropertiesProps = {
    system?: string;
    parse?: (settings: any) => any;
    format?: (values: any) => any;
    properties?: any[];
    partnerProperties?: any[];
    template?: (props: any) => React.ReactNode;
    header?: React.ReactNode;
    loading?: boolean;
    [key: string]: any;
};

type DefaultPropertiesState = {
    showEdit: boolean;
    showConfirm: boolean;
    settings: any;
    refresh?: boolean;
    partners?: any[];
    editRow?: any;
    disableEdit?: boolean;
    disableConfirm?: boolean;
    // Referenced by the EventModal below but never actually set via setState
    // anywhere in this component (pre-existing; EventModal always receives
    // undefined here). Preserved as-is -- not in scope to fix a type-only migration.
    eventId?: any;
};

const DefaultProperties = (props: DefaultPropertiesProps) => {
    const [state, setStateRaw] = useState<DefaultPropertiesState>({
        showEdit: false,
        showConfirm: false,
        settings: {},
    });

    // Mimic React.Component.setState: merge a partial (object or updater
    // function) into the existing state. Keeps the exact partial-merge
    // semantics the class relied on -- including when `setState` is handed to
    // consumers via the `template` prop below.
    const setState = useCallback(
        (
            update:
                | Partial<DefaultPropertiesState>
                | ((prev: DefaultPropertiesState) => Partial<DefaultPropertiesState>)
        ) => {
            setStateRaw((prev) => ({
                ...prev,
                ...(typeof update === 'function' ? update(prev) : update),
            }));
        },
        []
    );

    // Caches the partners fetch the way the class did via state.partners, but
    // in a ref so the guard reads the latest value regardless of closure age.
    const partnersRef = useRef<any[] | undefined>(undefined);

    // Same closure-age hazard for settings: getTableData is captured once into the
    // partner table's pinned config (setting_modifier's useState(config)), so it must
    // read the freshest settings from a ref rather than the render-time `state.settings`.
    // Otherwise a partner-specific save reloads settings but the pinned loader keeps
    // returning the initial (empty) closure value, so the save never appears (LB-294).
    const settingsRef = useRef<any>({});

    const getSystem = () => props.system || "Defaults";

    const loadSettings = async () => {
        let system = getSystem();
        let settings = (await axios.get(Config.api + `/odata/Company/Functions.GetSystemSettings?system=${system}`)).data;
        if(props.parse){
            settings = props.parse(settings);
        }
        settingsRef.current = settings;
        setState((prev) => ({settings, refresh: !prev.refresh}));
    };

    const loadPartners = async () => {
        if(partnersRef.current){
            return partnersRef.current;
        }
        let partners = (await axios.get(Config.api + `/api/v1/partners`)).data.Body.Partners;
        partnersRef.current = partners;
        setState({partners});
        return partners;
    };

    const getTableData = async () => {
        let partners = await loadPartners();
        return partners.map((row: any) => {
            var partnerSettings = ((settingsRef.current || {}).PartnerProperties || []).find((r: any) => r.PartnerCoId === row.Id);
            partnerSettings = partnerSettings || {};
            var ret: any = {PartnerName: row.CompanyName, AccountNumber: row.Id};
            Object.assign(ret, partnerSettings);
            if(props.parse){
                ret = props.parse(ret);
            }
            return ret;
        });
    };

    const saveRow = async (e: any) => {
        let system = getSystem();
        setState({disableEdit: true});
        let url = Config.api + `/odata/Company/Functions.SetSystemSettings?system=${system}`;
        if(e.AccountNumber){
            url += `&partnerId=${e.AccountNumber}`;
        }
        let data = {...e};
        if(props.format){
            data = props.format(data);
        }
        // Not a final-form `validate`: that sets `invalid`, which disables Save,
        // and Save has to stay clickable so the value can be pruned and retried.
        let oversized = Object.keys(data).some((key) => typeof data[key] === 'string' && data[key].length > GLOBAL_CONFIG_MAX_LENGTH);
        if(oversized){
            setState({disableEdit: false});
            return {[FORM_ERROR]: OVERSIZED_SETTING_ERROR};
        }
        try {
            await axios.put(url, data);
        } catch (error: any) {
            // Returned, not thrown, so submitError lands on the form that
            // actually submitted -- a dialog failure must not paint the page
            // behind it. Skipping loadSettings leaves the form dirty for a retry.
            setState({disableEdit: false});
            return {[FORM_ERROR]: extractSaveError(error)};
        }
        await loadSettings();
        // loadSettings already toggles `refresh` once. Toggling it again here
        // nets to zero under React 18 automatic batching (the two updates
        // collapse into one commit), so <EventTable refresh={state.refresh}>
        // would never see a change and the events list would not reload after a
        // save. Just close the edit dialog and let loadSettings' single toggle
        // drive the refresh.
        setState({showEdit: false});
        clearDefaultSettingsCache();
    };

    const editRow = async (row: any) => {
        setState({showEdit: true, editRow: row, disableEdit: false});
    };

    const deleteRow = async () => {
        let system = getSystem();
        setState({disableConfirm: true});
        let keys = Object.keys(state.editRow);
        let data: any = {};
        for(let i = 0; i < keys.length; i++){
            data[keys[i]] = "";
        }
        try {
            await axios.put(Config.api + `/odata/Company/Functions.SetSystemSettings?system=${system}&partnerId=${state.editRow.AccountNumber}`, data);
        } catch (error: any) {
            // Without this, disableConfirm stays true and the confirm dialog is
            // unclosable: both buttons disabled, onClose refusing backdrop and
            // escape. No submitError to render into here -- it is an onClick,
            // not a submit -- so report it centrally instead.
            reportError(error, {component: 'DefaultProperties.deleteRow', system});
            setState({disableConfirm: false});
            return;
        }
        await loadSettings();
        // See saveRow: loadSettings already toggles `refresh`; a second toggle
        // here cancels it out under React 18 batching, so just close the
        // confirm dialog and let loadSettings' single toggle drive the refresh.
        setState({showConfirm: false});
        clearDefaultSettingsCache();
    };

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let canEdit = Auth.hasPermission('settings/manage');
    if(props.template) {
        return props.template({
            state: state,
            setState: setState,
            getTableData: getTableData,
            editRow: editRow,
            saveRow: saveRow,
            deleteRow: deleteRow,
            ...props
        });
    }

    let properties = props.properties || [];
    let partnerProperties = props.partnerProperties || properties;
    let columns: any[] = [
      { id: "AccountNumber", filterable: true, sortable: true, label: 'Account Number', width: '13em' },
      { id: 'PartnerName', filterable: true, sortable: true, label: 'Company Name', width: '25em' }
    ];
    columns = columns.concat(partnerProperties.map((r: any) => ({id: r.name, label: r.label, filterable: true, sortable: true, template: r.type === 'checkbox' ? checkboxTemplate : undefined})));
    columns.push({ command: 'commands', stopPropagation: 'true', width: '15em', template: (value: any, row: any) => <Fragment>
        <Button variant='outlined' style={{marginRight: '1rem'}} size='small' disabled={!canEdit} onClick={() => editRow(row)}>Edit</Button>
        <Button variant='outlined' size='small' disabled={!canEdit} onClick={() => setState({editRow: row, showConfirm: true, disableConfirm: false})}>Delete</Button>
    </Fragment>});
    let config = {
        getData: getTableData,
        columns: columns,
      order: 'asc',
      orderBy: 'PartnerName',
      keyField: 'AccountNumber',
      pageSize: 20,
      pageSizes: [10, 20, 50, 100],
      refresh: state.refresh,
      title: `Partner Specific Settings`
    };
    let filter = {filters: [
          {field: "EventTypeId", operator: 'eq', value: 73},
          {field: "Category", operator: 'eq', value: props.system},
      ], logic: 'and'};

    if(props.loading){
        return <Grid container spacing={2}>
            <Grid size={{ md: 12 }}>
                <ContentLoader preserveAspectRatio='none' style={{height: '10em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
            </Grid>
            <Grid size={{ md: 12 }}>
                <ContentLoader preserveAspectRatio='none' style={{height: '15em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
            </Grid>
        </Grid>
    }
    return (
        <Grid container spacing={2}>
            <Grid size={{ md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>{props.header}</Toolbar>
                        <CardContent>
                            <Form onSubmit={saveRow} initialValues={state.settings} render={({ handleSubmit, pristine, hasValidationErrors, values, submitError }: any) => {
                                  return (
                                      <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', maxWidth: '900px'}}>
                                      {properties.map((r: any) => <div key={r.name} style={{display: 'flex'}}>
                                        <Field style={{flex: 1}} parse={identity} disabled={!canEdit} component={TextField} {...toFieldProps(r)}/>
                                      </div>)}
                                      {submitError && <Alert severity="error" style={{marginTop: '1em'}}>{submitError}</Alert>}
                                       <div style={{textAlign: 'left', marginBottom: '.5em', marginTop: '0.5em'}}>
                                        {/* hasValidationErrors, not invalid: final-form folds submit
                                            errors into `invalid`, so gating on `invalid` would grey out
                                            Save as soon as a save fails, leaving no way to retry. */}
                                        <DownloadButton onClick={handleSubmit} disabled={pristine || hasValidationErrors || !canEdit} variant='contained' color='primary'>Save</DownloadButton>
                                       </div>
                                      </form>
                                  );}}/>
                        </CardContent>
                </Card>
            </Grid>
            {partnerProperties && partnerProperties.length > 0 && <Grid size={{ md: 12 }}>
                <EnhancedTable config={config}/>
            </Grid>}
            <Grid size={12}>
                <EventTable refresh={state.refresh} title="Events" filter={filter}/>
            </Grid>
            <EventModal eventId={state.eventId}/>
            <Dialog
                maxWidth="sm"
                aria-labelledby="confirmation-dialog-title"
                open={state.showConfirm}
                onClose={(event: any, reason: string) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (state.disableConfirm) return;
                  }
                  setState({showConfirm: false});
                }}>
                <Toolbar className='lbtoolbar'>Confirm Delete</Toolbar>
                <DialogContent>
                  <Typography>Are you sure you want to delete these settings?</Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setState({showConfirm: false})} disabled={state.disableConfirm} color="primary">
                    No
                  </Button>
                  <Button onClick={deleteRow} disabled={state.disableConfirm} color="primary">
                    Yes
                  </Button>
                </DialogActions>
              </Dialog>
            <Dialog
                maxWidth="sm"
                open={state.showEdit}
                onClose={(event: any, reason: string) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (state.disableEdit) return;
                  }
                  setState({showEdit: false});
                }}
                fullWidth={true}>
                {state.editRow ?
                    <Form onSubmit={saveRow}
                    initialValues={state.editRow}
                    render={({ handleSubmit, pristine, hasValidationErrors, values, submitError }: any) => (
                      <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                      <Toolbar className='lbtoolbar'>Edit Settings</Toolbar>
                    <DialogContent>
                    <div style={{display: 'flex', flexDirection: 'column', flexShrink: '0'}}>
                    {partnerProperties.map((r: any) => <div key={r.name} style={{display: 'flex'}}>
                      <Field style={{flex: 1}} parse={identity} component={TextField} {...toFieldProps(r)}/>
                    </div>)}
                      </div>
                      {submitError && <Alert severity="error" style={{marginTop: '1em'}}>{submitError}</Alert>}
                    </DialogContent>
                    <DialogActions>
                      <Button type='submit' disabled={pristine || hasValidationErrors || state.disableEdit} color="primary">
                        Save
                      </Button>
                    </DialogActions>
                    </form>
                    )}/>
                     :  <DialogContent><LinearProgress style={{height: '2em'}} variant="indeterminate"/></DialogContent> }

              </Dialog>
        </Grid>
    );
};

const AckProperties = (props: any) => {
    useEffect(() => {
        if(props.setTitle) {
            props.setTitle('Ack. Settings', true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const properties = [
        {name: 'DefaultShipDate', label: 'Default Days to Ship', validate: validateNumber},
        {name: 'DefaultShipBusinessDays', label: 'Exclude Weekends', component: SwitchField, type: 'checkbox'},
        {name: 'DefaultCancelReason', label: 'Default Cancellation Reason', component: AutoComplete, options: ChangeReasonOptions}
    ];
    const format = (values: any) => {
        let v = {...values};
        if(v.DefaultShipBusinessDays){
            v.DefaultShipBusinessDays = v.DefaultShipBusinessDays.toString();
        } else {
            v.DefaultShipBusinessDays = "";
        }
        return v;
    };

    const parseSettings = (v: any) => {
        v.DefaultShipBusinessDays = v.DefaultShipBusinessDays === 'true';
        return v;
    };

    return <DefaultProperties header='Acknowledgement Settings' system="Acknowledgement" parse={parseSettings} format={format} properties={properties} {...props} />;
};

const ReturnProperties = (props: any) => {
    useEffect(() => {
        if(props.setTitle) {
            props.setTitle('Return Reasons', true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const properties = [
        {name: 'DefaultReturnReason', label: 'Default Return Reason', component: AutoComplete, options: ReturnReasonOptions}
    ];
    const format = (values: any) => {
        let v = {...values};
        if(v.DefaultReturnReason){
            let lowerReason = v.DefaultReturnReason.toLowerCase();
            let code: any = ReturnCodes.find((r: any) => r.label.toLowerCase() === lowerReason);
            code = code ? code.value : null;
            if(!code){
                code = "16";
            }
            v.DefaultReturnCode = code;
        } else {
            v.DefaultReturnCode = "";
        }
        return v;
    };

    return <DefaultProperties header='Default Return Settings' system="Return" format={format} properties={properties} {...props} />;
};

const ScoreCardProperties = (props: any) => {
    useEffect(() => {
        if(props.setTitle) {
            props.setTitle('Scorecard Settings', true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const properties = [
        {name: 'ScorecardShipmentInterval', label: 'Shipment Interval (hours)'},
        {name: 'ScorecardCancelPercent', label: 'Cancellation Percentage'},
        {name: 'ScorecardBusinessDays', label: 'Exclude weekends', component: SwitchField, type: 'checkbox'}
    ];
    const format = (values: any) => {
        let v = {...values};
        if(v.ScorecardBusinessDays){
            v.ScorecardBusinessDays = v.ScorecardBusinessDays.toString();
        } else {
            v.ScorecardBusinessDays = "";
        }
        return v;
    };

    const parseSettings = (v: any) => {
        v.ScorecardBusinessDays = v.ScorecardBusinessDays === 'true';
        return v;
    };

    return <DefaultProperties header='Scorecard Settings' system="ScoreCards" parse={parseSettings} format={format} properties={properties} {...props} />;
};

const CatalogImportExportProperties = (props: any) => {
    const [state, setState] = useState<any>({loading: true});

    const parseSettings = (v: any) => {
        v.CatalogSkipImageDownload = v.CatalogSkipImageDownload === 'true';
        return v;
    };
    const format = (values: any) => {
        let v = {...values};
        if(v.CatalogSkipImageDownload){
            v.CatalogSkipImageDownload = v.CatalogSkipImageDownload.toString();
        } else {
            v.CatalogSkipImageDownload = "";
        }
        return v;
    };

    useEffect(() => {
        // load profiles shared at company level
        (async () => {
            let exportProfiles = (await loadProfiles('RetailerCatalog')).filter((r: any) => !(!r.Id)).map((r: any) => ({label: r.Name, value: r.Name}));
            let importProfiles = (await loadProfiles('SupplierCatalog')).filter((r: any) => !(!r.Id)).map((r: any) => ({label: r.Name, value: r.Name}));
            let properties: any[] = [
                {name: 'CatalogImportTemplate', label: 'Import Profile', options: importProfiles || [], component: AutoComplete}
            ];
            if(props.isBillable){
                properties.push({name: 'CatalogSkipImageDownload', label: 'Host Images Outside Logicbroker', component: SwitchField, type: 'checkbox'});
            }
            setState((prev: any) => ({...prev, properties: properties,
                 partnerProperties: [{name: 'CatalogExportTemplate', label: 'Export Profile', options: exportProfiles || [], component: AutoComplete}],
                  loading: false}));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <DefaultProperties loading={state.loading} header='Catalog Import/Export Settings' system="Catalog" parse={parseSettings} format={format} properties={state.properties} partnerProperties={state.partnerProperties} {...props} />;
};

const ShipmentProperties = (props: any) => {
    const validateCompanyId = (v: any) => {
        let maxLength = 13;
        if(v && v.length > maxLength){
            return `Maximum length is ${maxLength} digits.`;
        }
        if(v && /^\d+$/.test(v) === false){
            return `Identifier must be a number.`;
        }
        return undefined;
    };
    const properties: any[] = [
        {name: 'UCC128CompanyId', label: 'UCC 128 (GS1) Company Identifier', validate: validateCompanyId}
    ];
    // Typed any[] -- properties/billableProperties have different shapes
    // (validate vs. component+type) and are later combined via .concat() in
    // the mount effect, which TS won't allow across mismatched array literal
    // element types.
    const billableProperties: any[] = [
        {name: 'CopyShipMethodFromOrder', label: 'Copy Ship Method From Order', component: SwitchField, type: 'checkbox'},
        {name: 'CreateInvoiceFromShipment', label: 'Create Invoice From Shipment', component: SwitchField, type: 'checkbox'}
    ];
    const billablePartnerProperties: any[] = [
        {name: 'UseOrderShipMethod', label: 'Copy Ship Method From Order', component: SwitchField, type: 'checkbox'},
        {name: 'AutoInvoiceFromShipment', label: 'Create Invoice From Shipment', component: SwitchField, type: 'checkbox'}
    ];
    // Pre-migration (git show ee28306a:src/pages/settings/default_properties.js)
    // state.partnerProperties was hardcoded to `[]` for the ShipmentProperties
    // wrapper, so non-billable companies never saw the "Partner Specific
    // Settings" override table (DefaultProperties.render() only renders it when
    // `partnerProperties.length > 0`); billable companies get it via
    // the mount effect below swapping in billablePartnerProperties. Preserved
    // that behavior verbatim (initial partnerProperties = []).
    const [state, setState] = useState<any>({partnerProperties: [], properties: properties});

    useEffect(() => {
        if(props.setTitle) {
            props.setTitle('Shipment Settings', true);
        }
        if(props.isBillable){
            setState((prev: any) => ({
                ...prev,
                properties: properties.concat(billableProperties),
                partnerProperties: billablePartnerProperties
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const format = (values: any) => {
        let v = {...values};
        if(v.CopyShipMethodFromOrder){
            v.CopyShipMethodFromOrder = v.CopyShipMethodFromOrder.toString();
        } else {
            v.CopyShipMethodFromOrder = "";
        }
        if(v.CreateInvoiceFromShipment){
            v.CreateInvoiceFromShipment = v.CreateInvoiceFromShipment.toString();
        } else {
            v.CreateInvoiceFromShipment = "";
        }
        if(v.AutoInvoiceFromShipment){
            v.AutoInvoiceFromShipment = v.AutoInvoiceFromShipment.toString();
        } else {
            v.AutoInvoiceFromShipment = "";
        }
        if(v.UseOrderShipMethod){
            v.UseOrderShipMethod = v.UseOrderShipMethod.toString();
        } else {
            v.UseOrderShipMethod = "";
        }
        return v;
    };

    const parseSettings = (v: any) => {
        v.CopyShipMethodFromOrder = v.CopyShipMethodFromOrder === 'true';
        v.CreateInvoiceFromShipment = v.CreateInvoiceFromShipment === 'true';
        v.AutoInvoiceFromShipment = v.AutoInvoiceFromShipment === 'true';
        v.UseOrderShipMethod = v.UseOrderShipMethod === 'true';
        return v;
    };

    return <DefaultProperties header='Shipment Settings' parse={parseSettings} format={format} system="Shipment" properties={state.properties} partnerProperties={state.partnerProperties} {...props} />;
};

const switchSx = (theme: any) => ({
    display: "flex",
    justifyContent: "center",
    "& .Mui-checked": {
        color: `${theme.palette.tertiary?.main || theme.palette.primary.main} !important`,
    }
});

export function PocGeneralProperties(props: any){
    const properties = [
        {
            name: 'POCDefaultAttributeSet',
            label: 'Default Attribute Set',
            component: (componentProps: any) => <FormEnhancedSelect {...props} {...componentProps} />,
            type: 'select'
        },
        {
            name: 'CatalogManagerStoreImages',
            label: 'Use CDN for Images',
            component: (componentProps: any) => <SwitchField {...componentProps} sx={switchSx}/>,
            type: 'checkbox'
        },
        {
            name: 'POCIgnoreImportValidation',
            label: 'Allow Import with Missing Requirements',
            component: (componentProps: any) => <SwitchField {...componentProps} sx={switchSx}/>,
            type: 'checkbox'
        },
        {
            name: 'POCAutoApproveNew',
            label: 'Auto Approve New Products from Partners',
            component: (componentProps: any) => <SwitchField {...componentProps} sx={switchSx}/>,
            type: 'checkbox'
        },
        {
            name: 'POCEnforceCategoryMatch',
            label: 'Enforce Category Matching',
            component: (componentProps: any) => <SwitchField {...componentProps} sx={switchSx}/>,
            type: 'checkbox',
            // Partner table column only: starts hidden behind the Columns toggle
            // so the default table stays as wide as it was.
            hidden: true
        }
    ];

    const parseSettings = (values: any) => {
        values.CatalogManagerStoreImages = values.CatalogManagerStoreImages === 'true' || values.CatalogManagerStoreImages === 'True';
        values.POCIgnoreImportValidation = values.POCIgnoreImportValidation === 'true' || values.POCIgnoreImportValidation === 'True';
        values.POCAutoApproveNew = values.POCAutoApproveNew === 'true' || values.POCAutoApproveNew === 'True';
        values.POCEnforceCategoryMatch = values.POCEnforceCategoryMatch === 'true' || values.POCEnforceCategoryMatch === 'True';
        return values;
    }

    const formatSettings = (values: any) => {
        let v = {...values};
        if(v.CatalogManagerStoreImages){
            v.CatalogManagerStoreImages = v.CatalogManagerStoreImages.toString();
        } else {
            v.CatalogManagerStoreImages = "";
        }
        if(v.POCIgnoreImportValidation){
            v.POCIgnoreImportValidation = v.POCIgnoreImportValidation.toString();
        } else {
            v.POCIgnoreImportValidation = "";
        }
        if(v.POCAutoApproveNew){
            v.POCAutoApproveNew = v.POCAutoApproveNew.toString();
        } else {
            v.POCAutoApproveNew = "";
        }
        if(v.POCEnforceCategoryMatch){
            v.POCEnforceCategoryMatch = v.POCEnforceCategoryMatch.toString();
        } else {
            v.POCEnforceCategoryMatch = "";
        }

        return v;
    }


    return (
        <DefaultProperties
            header='General Settings'
            system="ProductOnboardingCenter"
            parse={parseSettings}
            format={formatSettings}
            properties={properties}
            template={props.template}
        />
    )
}

const AUTO_APPROVE_MECHANISM = "POC monitors for updates to these attributes, and when a supplier updates any attribute(s) in this group, then all updates are Approved for that product. For example, if price is an Auto Approve attribute, and the supplier updates price, compare_at_price, and description, then all 3 attributes will be Approved in My Catalog.";

export const POC_APPROVE_ALL_DESCRIPTION = "Approves every attribute change a supplier sends, whichever attributes changed. This is a separate setting from Approve Changes for Selected Attributes below and takes precedence over it while enabled. Applies to every partner that has no override of its own.";

export const POC_APPROVE_ALL_PARTNER_DESCRIPTION = "Overrides Approve All Attribute Changes for this partner only. Inherited from All Partners leaves this partner following whatever the All Partners setting is. This is a separate setting from Approve Changes for Selected Attributes below.";

export const POC_AUTO_APPROVE_DESCRIPTION = AUTO_APPROVE_MECHANISM + " This is a separate setting from Approve All Attribute Changes above and is only in effect while that setting is off. Applies to every partner that has no override of its own.";

export const POC_AUTO_APPROVE_PARTNER_DESCRIPTION = AUTO_APPROVE_MECHANISM + " This list overrides the All Partners list for this partner only. It is a separate setting from Approve All Attribute Changes above and is only in effect while approve-all is off for this partner.";

export const toPartnerApproveAll = (raw: any) => {
    if(raw === true || raw === 'true' || raw === 'True') return 'true';
    if(raw === false || raw === 'false' || raw === 'False') return 'false';
    return '';
};

const isGlobalApproveAllOn = (globalSettings: any) => {
    const raw = (globalSettings || {}).POCAutoApproveAllUpdates;
    return raw === true || raw === 'true' || raw === 'True';
};

// Partner scope is tri-state: an unset partner falls back to the global setting,
// so it is distinct from an explicit "Off". The inherit option names the value
// it is inheriting, so the row states the setting actually in force rather than
// sending the user to the global card to work it out.
export const partnerApproveAllOptions = (globalSettings?: any) => [
    {value: '', label: isGlobalApproveAllOn(globalSettings) ? 'On (Inherited from All Partners)' : 'Off (Inherited from All Partners)'},
    {value: 'true', label: 'On'},
    {value: 'false', label: 'Off'},
];

// 16px matches MuiFormLabel, which is what EnhancedSelect heads setting 2 with.
const settingHeadingStyle = {color: 'black', fontWeight: 'bold', fontSize: '16px'};

// Typography, not a div: EnhancedSelect renders its description through one, so
// a div inherits the card's 16px where the section below it resolves body1 to
// 14px, and the two subsections disagree on size.
const SettingDescription = ({children}: any) => (
    <Typography style={{textAlign: 'left', color: '#c0c0c0', paddingBottom: '10px'}}>{children}</Typography>
);

// Renders the label and description above the switch so setting 1 reads as its
// own subsection, the way EnhancedSelect already does for setting 2.
export const ApproveAllSwitchField = (componentProps: any) => (
    // textAlign: the settings card centres its text, and unlike EnhancedSelect
    // this block has no styling of its own to opt out with.
    <div style={{textAlign: 'left', flex: 1}}>
        <div style={settingHeadingStyle}>Approve All Attribute Changes</div>
        <SettingDescription>{POC_APPROVE_ALL_DESCRIPTION}</SettingDescription>
        <SwitchField {...componentProps} sx={switchSx}/>
    </div>
);

// The partner dialog's counterpart to ApproveAllSwitchField: AutoComplete only
// offers a floating input label, which is not the heading the global card uses.
// label is dropped rather than forwarded -- it stays on the property to title
// the partner table's column, and inside the input it would repeat the heading.
export const ApproveAllSuggestField = ({label, ...componentProps}: any) => (
    <div style={{textAlign: 'left', flex: 1}}>
        <div style={settingHeadingStyle}>Approve All Attribute Changes</div>
        <SettingDescription>{POC_APPROVE_ALL_PARTNER_DESCRIPTION}</SettingDescription>
        <AutoComplete {...componentProps}/>
    </div>
);

export function PocAutoApprovalProperties(props: any){
    const properties = [
        {
            name: 'POCAutoApproveAllUpdates',
            label: 'Approve All Attribute Changes',
            sectionHeader: 'Auto Approve Attribute Changes',
            component: ApproveAllSwitchField,
            type: 'checkbox'
        },
        {
            name: 'POCAutoApproveUpdatedFields',
            label: 'Approve Changes for Selected Attributes',
            component: (componentProps: any) => <FormEnhancedSelect {...componentProps} {...props} description={POC_AUTO_APPROVE_DESCRIPTION} />,
            type: 'select',
            // Approve-all supersedes the list, so setting 2 greys out while it is on.
            disabledWhen: (values: any) => !!values.POCAutoApproveAllUpdates
        },
    ];

    const parseSettings = (values: any) => {
        if(values.POCAutoApproveUpdatedFields) {
            values.POCAutoApproveUpdatedFields = values.POCAutoApproveUpdatedFields.split(",");
        }
        if(values.AccountNumber){
            // Stays a string so unset stays distinguishable from an explicit off.
            values.POCAutoApproveAllUpdates = toPartnerApproveAll(values.POCAutoApproveAllUpdates);
        } else {
            // Nothing to inherit from at global scope, so missing reads as off.
            values.POCAutoApproveAllUpdates = values.POCAutoApproveAllUpdates === true
                || values.POCAutoApproveAllUpdates === 'true'
                || values.POCAutoApproveAllUpdates === 'True';
        }
        return values;
    }

    const formatSettings = (values: any) => {
        if(Array.isArray(values.POCAutoApproveUpdatedFields)) {
            values.POCAutoApproveUpdatedFields = values.POCAutoApproveUpdatedFields.join(",");
        }
        if(values.AccountNumber){
            if(values.POCAutoApproveAllUpdates !== 'true' && values.POCAutoApproveAllUpdates !== 'false'){
                // Use Global Settings has to erase the stored override, and ""
                // is this endpoint's erase sentinel (see deleteRow). Omitting
                // the key instead means "leave unchanged", which silently keeps
                // whatever the partner was pinned to before.
                values.POCAutoApproveAllUpdates = '';
            }
            // 'true'/'false' are written literally as the explicit override.
        } else if(values.POCAutoApproveAllUpdates){
            values.POCAutoApproveAllUpdates = 'true';
        } else {
            values.POCAutoApproveAllUpdates = '';
        }
        return values;
    }

    // Clears the partner's overrides in form state so the user does not have to
    // remove attributes one at a time. Still needs a Save to persist.
    const partnerResetToGlobal = (form: any) => {
        form.change('POCAutoApproveUpdatedFields', []);
        form.change('POCAutoApproveAllUpdates', '');
    };

    return (
        <DefaultProperties
            header='Auto Approval Settings'
            system="ProductOnboardingCenter"
            parse={parseSettings}
            format={formatSettings}
            properties={properties}
            partnerResetToGlobal={partnerResetToGlobal}
            partnerProperties={[
                {
                    name: 'POCAutoApproveAllUpdates',
                    label: 'Approve All Attribute Changes',
                    component: ApproveAllSuggestField,
                    // Resolved per render rather than pinned here, because the
                    // inherit option's label reports the current global value.
                    optionsFrom: partnerApproveAllOptions,
                    // Without this the table prints the raw stored value, so an
                    // unset partner shows an empty cell instead of saying which
                    // setting is actually in force.
                    cellTemplate: (value: any, globalSettings: any) => {
                        const option = partnerApproveAllOptions(globalSettings).find((o) => o.value === toPartnerApproveAll(value));
                        return option ? option.label : '';
                    }
                },
                {
                    name: 'POCAutoApproveUpdatedFields',
                    label: 'Approve Changes for Selected Attributes',
                    type: 'select',
                    component: (componentProps: any) => <FormEnhancedSelect {...componentProps} {...props} description={POC_AUTO_APPROVE_PARTNER_DESCRIPTION} />,
                    // Grey the list only when approve-all is in effect for this
                    // partner. An explicit Off is the field-level case, so the
                    // list it approves against has to stay editable.
                    disabledWhen: (values: any, globalSettings: any) => {
                        if(values.POCAutoApproveAllUpdates === 'true') return true;
                        if(values.POCAutoApproveAllUpdates === 'false') return false;
                        return isGlobalApproveAllOn(globalSettings);
                    }
                }
            ]}
            template={props.template}
        />
    )
}

export function PocAutoIgnoreProperties(props: any){
    const properties = [
        {
            name: 'POCAutoIgnoreUpdatedFields',
            label: 'Auto Ignore Attribute Changes',
            component: (componentProps: any) => <FormEnhancedSelect {...componentProps} {...props} description={"Retailer changes to these attributes are always saved and applied. Any updates the supplier sends for these attributes will be ignored and will not overwrite the retailer's values."} />,
            type: 'select'
        },
    ];

    const parseSettings = (values: any) => {
        if(values.POCAutoIgnoreUpdatedFields) {
            values.POCAutoIgnoreUpdatedFields = values.POCAutoIgnoreUpdatedFields.split(",");
        }
        return values;
    }

    const formatSettings = (values: any) => {
        if(Array.isArray(values.POCAutoIgnoreUpdatedFields)) {
            values.POCAutoIgnoreUpdatedFields = values.POCAutoIgnoreUpdatedFields.join(",");
        }
        return values;
    }


    return (
        <DefaultProperties
            header='Auto Ignore Settings'
            system="ProductOnboardingCenter"
            parse={parseSettings}
            format={formatSettings}
            properties={properties}
            template={props.template}
        />
    )
}

export function PocConnectorProperties(props: any){
    const properties = [
        {
            name: 'POCFilterUpdatedFields',
            label: 'Filter updated fields',
            component: (componentProps: any) =>
                <FormEnhancedSelect {...componentProps} {...props} description={"If no fields are added here, then all Approved attribute updates will automatically post to your external system."} />,
            type: 'select'
        }
    ];

    const parseSettings = (values: any) => {
        if(values.POCFilterUpdatedFields) {
            values.POCFilterUpdatedFields = values.POCFilterUpdatedFields.split(",");
        }
        return values;
    }

    const formatSettings = (values: any) => {
        if(Array.isArray(values.POCFilterUpdatedFields)) {
            values.POCFilterUpdatedFields = values.POCFilterUpdatedFields.join(",");
        }
        return values;
    }


    return (
        <DefaultProperties
            header={"Connector Configurations"}
            system="ProductOnboardingCenter"
            parse={parseSettings}
            format={formatSettings}
            properties={properties}
            template={props.template}
        />
    )
}

export function PocChannelProperties(props: any){
    const history = useHistory();

    const properties = [
        {
            name: 'POCDefaultImportProfile',
            label: 'Default Mapping Profile',
            partnerOnly: true,
            // The only channel setting offered for non-marketplace (dropship) retailers.
            allowNonMarketplace: true,
            component: (componentProps: any) => {
                return (
                    <Grid container style={{flexDirection: "column"}}>
                        <FormEnhancedSelect {...props} {...componentProps} isMulti={false}
                                            options={componentProps.profiles.map((profile: any) => ({
                                                Name: profile.Name, FriendlyName: profile.Name
                                            }))}
                                            description={
                                                <Typography>
                                                    Set up your mapping profile to support automation around products.
                                                </Typography>
                                            }
                        />
                        <Grid container style={{justifyContent: "flex-start", gap: "20px", marginTop: "10px"}}>
                            <Button
                                variant={"contained"}
                                color={"primary"}
                                onClick={() => history.push(`/productonboardingcenter/import?partnerId=${componentProps.retailerId}`)}
                            >
                                Create New Profile
                            </Button>

                            <Button
                                variant={"contained"}
                                color={"primary"}
                                onClick={() => history.push(`/productonboardingcenter/import?partnerId=${componentProps.retailerId}&profile=${componentProps.input.value}`)}
                                disabled={!componentProps.lastImport || !componentProps.input.value}
                            >
                                Edit Existing Profile
                            </Button>
                        </Grid>
                    </Grid>
                );
            },
            type: 'select'
        },
        {
            name: 'POCAutoIgnoreUpdatedFields',
            label: 'Ignore Fields',
            component: (componentProps: any) =>
                <FormEnhancedSelect {...props} {...componentProps} isMulti={true} description={"Ignore these fields when sending product updates to channels."} />,
            type: 'select'
        },
        {
            name: 'POCNewListingStatus',
            label: 'Override default sync status of "Not Listed"',
            partnerOnly: true,
            component: (componentProps: any) =>
                <FormEnhancedSelect {...props} {...componentProps} isMulti={false} options={listingStatusOptions.filter((r: any) => r !== "All").map((r: any) => ({FriendlyName: r, Name: r}))}
                                    description={"If set, this will override the default sync status of \"Not Listed\" with the status applied below, only if the item does not already exist. This will not change the sync status of an existing item."}/>,
            type: 'select'
        },
        {
            name: 'POCUpdateListingStatus',
            label: 'Override default sync status of "Listing Pending"',
            partnerOnly: true,
            component: (componentProps: any) =>
                <FormEnhancedSelect {...props} {...componentProps} isMulti={false} options={listingStatusOptions.filter((r: any) => r !== "All").map((r: any) => ({FriendlyName: r, Name: r}))}
                                    description={"If set, this will override the default sync status of \"Listing Pending\" with the status applied below, only if a field on the product has changed.  If no fields are changed, we won't alter the sync status from whatever it currently is for that product in my catalog."}/>,
            type: 'select'
        }
    ];

    const parseSettings = (values: any) => {
        if(values.POCAutoIgnoreUpdatedFields) {
            values.POCAutoIgnoreUpdatedFields = values.POCAutoIgnoreUpdatedFields.split(",");
        }
        return values;
    }

    const formatSettings = (values: any) => {
        if(Array.isArray(values.POCAutoIgnoreUpdatedFields)) {
            values.POCAutoIgnoreUpdatedFields = values.POCAutoIgnoreUpdatedFields.join(",");
        }
        return values;
    }


    return (
        <DefaultProperties
            system="ProductOnboardingCenter"
            parse={parseSettings}
            format={formatSettings}
            properties={properties}
            template={props.template}
        />
    )
}

export {AckProperties,
    ReturnProperties,
    ScoreCardProperties,
    CatalogImportExportProperties,
    ShipmentProperties
};
